'use client';

import React, { startTransition } from 'react';
import { isDesktop } from '@benzinga/device-utils';
import useElementPosition, { Position } from './useElementPosition';

const NAVIGATION_HEADER_ID = 'navigation-header';
const AD_BANNER_IDS = ['hideRotatingBanner', 'hideStreamlineBanner'];

export const useDetectStickyHeader = (): {
  entry: IntersectionObserverEntry | null;
  isBreakingNewsBannerVisible: boolean;
  isStickyNavbar: boolean;
  position: Position;
} => {
  const [isStickyNavbar, setIsStickyNavbar] = React.useState(false);
  const [isBreakingNewsBannerVisible, setIsBreakingNewsBannerVisible] = React.useState(false);
  const [entry, setEntry] = React.useState<IntersectionObserverEntry | null>(null);
  const position = useElementPosition(NAVIGATION_HEADER_ID, { throttleMs: 100 });

  React.useEffect(() => {
    const hasIOSupport = !!window.IntersectionObserver;
    if (!hasIOSupport) return;
    const isStreamlineBannerHidden = AD_BANNER_IDS.some(id => localStorage.getItem(id) === 'true');

    const observer = new IntersectionObserver(
      ([entry]: IntersectionObserverEntry[]) => {
        const height = entry.boundingClientRect.height;
        const DESKTOP_HEIGHT = 132;
        const STREAMLINE_BANNER_HEIGHT = 70;
        const desktopHeightWithStreamlineBanner = DESKTOP_HEIGHT + STREAMLINE_BANNER_HEIGHT;
        const fallbackHeight = isStreamlineBannerHidden ? DESKTOP_HEIGHT : desktopHeightWithStreamlineBanner;
        const navbarHeight = isDesktop() ? (height ? height : fallbackHeight) : height;
        const isSticky = window.scrollY >= navbarHeight;
        setIsStickyNavbar(isSticky);
        setEntry(entry);
      },
      { root: null, threshold: [0, 1] },
    );
    const headerElement = document?.getElementById(NAVIGATION_HEADER_ID) as HTMLElement;
    if (headerElement) {
      observer.observe(headerElement);
    }

    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const breakingNewsBannerElement = document?.getElementById('breaking-news-banner') as HTMLElement;
    setIsBreakingNewsBannerVisible(!!breakingNewsBannerElement);
  }, []);

  return { entry, isBreakingNewsBannerVisible, isStickyNavbar, position };
};

export default useDetectStickyHeader;
